import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { leadTemps, legacyLeadTemps } from '../mock';
import { useLead, useLeadsLoaded, useLeadFetchState, useFullLead } from '../lib/leadsStore';
import {
  ArrowLeft, Phone, MessageSquare, Mail, Save, Send, MoreHorizontal,
  User2, Briefcase, Wallet, ShieldAlert, Home, FileText,
  Sparkles, CheckCircle2, Wand2,
  Flame, Sun, Snowflake, PhoneCall, Sprout, Ban, XCircle, ArrowUpRight,
  StickyNote, Rows3, Columns2, ChevronDown, Check, Archive, RotateCcw,
  ShieldX, DollarSign, CreditCard, Building2, UserCircle2, Crown, Shuffle, X,
  CalendarCheck, BadgeCheck, Target, Megaphone, ClipboardList, CalendarPlus,
  Send as SendIcon, FileSignature, Trophy, FileCheck, Lock, Hand, Cloud, AlertTriangle, Hash,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { useAuth } from '../lib/authStore';
import NotePad from '../components/notes/NotePad';
import BuildFromNotesModal from '../components/notes/BuildFromNotesModal';
import AppointmentScheduler from '../components/notes/AppointmentScheduler';
import CoBorrowerNudge from '../components/notes/CoBorrowerNudge';
import { LeadsStore } from '../lib/leadsStore';
import { useTeam, TeamStore } from '../lib/teamStore';
import { LEAD_SOURCES, normalizeSource } from '../lib/leadSources';
import { createPortal } from 'react-dom';
import { calcMonthly, money, buildInitial, sectionCompletion } from '../components/lead-profile/helpers';
import { DatePopover } from '../components/ui/date-popover';
import FundedConfirmModal from '../components/pipeline/FundedConfirmModal';
import { Stat, PayRow } from '../components/lead-profile/atoms';
import {
  ProfileSection, CoBorrowerSection, EmploymentTab, AssetsSection,
  LiabilitiesTab, PropertySection, LoanSection,
} from '../components/lead-profile/sections';

// TEMP icon + style map. Includes the new active pipeline values
// (hot / appointment / pre_approved / strike_rate / second_voice) and
// the legacy values so older lead records still render their pill.
const TEMP_ICONS = {
  hot: Flame,
  appointment: CalendarCheck,
  pre_approved: BadgeCheck,
  strike_rate: Target,
  second_voice: Megaphone,
  follow_up: PhoneCall,
  warm: Sun,
  cold: Snowflake,
  nurture: Sprout,
  dnc: Ban,
  dead: XCircle,
};
const TEMP_STYLES = {
  hot:          'bg-red-50 text-red-700 border-red-200',
  appointment:  'bg-[hsl(var(--kf-gold-soft))] text-[hsl(var(--kf-gold-deep))] border-[hsl(var(--kf-gold))]/40',
  pre_approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  strike_rate:  'bg-sky-50 text-sky-700 border-sky-200',
  second_voice: 'bg-violet-50 text-violet-700 border-violet-200',
  // legacy — display only
  follow_up:    'bg-[hsl(var(--kf-gold-soft))] text-[hsl(var(--kf-gold-deep))] border-[hsl(var(--kf-gold))]/40',
  warm:         'bg-orange-50 text-orange-700 border-orange-200',
  cold:         'bg-sky-50 text-sky-700 border-sky-200',
  nurture:      'bg-emerald-50 text-emerald-700 border-emerald-200',
  dnc:          'bg-neutral-200 text-neutral-700 border-neutral-300',
  dead:         'bg-neutral-100 text-neutral-500 border-neutral-200',
};

// Single lookup that resolves a temp key to its display label across
// BOTH active + legacy lists.
const ALL_TEMPS_BY_KEY = Object.fromEntries(
  [...leadTemps, ...legacyLeadTemps].map((t) => [t.key, t])
);

const SECTIONS = [
  { key: 'profile', label: 'Profile', icon: User2 },
  { key: 'property', label: 'Property', icon: Home },
  { key: 'employment', label: 'Income', icon: Briefcase },
  { key: 'assets', label: 'Assets', icon: Wallet },
  { key: 'liabilities', label: 'Credit', icon: ShieldAlert },
  { key: 'loan', label: 'Loan Terms', icon: FileText },
  { key: 'notes', label: 'Notes', icon: StickyNote },
  { key: 'summary', label: 'Summary', icon: ClipboardList },
];

export default function LeadProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const lead = useLead(id);
  const fullLead = useFullLead(id);
  const loaded = useLeadsLoaded();
  // ── ALWAYS fetch the full lead doc on profile mount ──────────────────
  // The list-cache only holds the `light=true` projection (no
  // `application.currentAddress`, no SSN, etc.), so even if a lead is
  // available via `useLead` we still need to fire a GET /leads/{id} to
  // hydrate the form. `fetchById` is idempotent + dedupes concurrent
  // calls and seeds the full doc into `_byId`, which `getMergedLead`
  // now prefers — so the form re-renders with complete data the moment
  // the response lands. Without this, refreshing the profile re-hydrates
  // from the stripped projection and the LO sees blanks for any field
  // not in the light allow-list.
  const { loading: byIdLoading, notFound: byIdNotFound } = useLeadFetchState(id);
  useEffect(() => {
    if (!id) return;
    if (byIdLoading) return;    // a fetch is already in flight
    if (byIdNotFound) return;   // backend already said 404
    LeadsStore.fetchById(id);
  }, [id, byIdLoading, byIdNotFound]);
  const [data, setData] = useState(() => buildInitial(lead));
  // Co-borrower draft state — mirrors lead.co_borrower locally so typing is
  // instant (no per-keystroke server round-trip) and debounce-flushed to Mongo.
  // Without this, every keystroke fired a PATCH; out-of-order responses stomped
  // over the user's in-progress typing, producing the "glitchy" feel reported
  // on co-borrower DOB / phone / SSN fields.
  const [coData, setCoData] = useState(() => lead?.co_borrower || null);
  // Always start hydrated=false so the mount-time useEffect runs once and gets a
  // chance to consume any localStorage stash (kf_pending_app_<id>) written by
  // the Create-Lead modal before navigation.
  const [hydrated, setHydrated] = useState(false);
  const [section, setSection] = useState('profile');
  const [buildOpen, setBuildOpen] = useState(false);
  const [pushingArive, setPushingArive] = useState(false);
  // Confirm modal — opens when LO clicks "Resend to Arive" on a lead that
  // has already been pushed. Prevents accidental duplicate loan creation
  // in ARIVE (ARIVE creates a brand new loan on every webhook hit).
  const [resendConfirmOpen, setResendConfirmOpen] = useState(false);
  // Save status surfaced in the header — 'idle' (saved/no edits), 'pending'
  // (debounce in flight), 'saving' (PATCH inflight), 'error' (last PATCH
  // failed). Drives the "Saved · just now / Saving… / Unsaved" pill so the
  // user can SEE whether the latest edit hit Mongo before they push to ARIVE.
  const [saveStatus, setSaveStatus] = useState('idle');
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const completion = useMemo(() => sectionCompletion(data), [data]);

  // Refs to track per-lead hydration so React StrictMode's double-invoke
  // doesn't wipe values applied on the first pass.
  const hydratedLeadIdsRef = useRef(new Set());

  // PERSISTENCE CONTRACT for the 1003 form (`data` state):
  // - User edits → `setData` updates local state instantly (UI feels snappy).
  // - 800ms debounce → save full `application` payload to Mongo via PATCH.
  // - `flushPendingSave()` bypasses the debounce and awaits the server write
  //   — used by pushToArive() and the unmount cleanup so changes can't be
  //   lost between keystroke and navigation/push.
  const pendingSaveRef = useRef({ timer: null, data: null });

  const flushPendingSave = useCallback(async () => {
    const p = pendingSaveRef.current;
    if (p.timer) {
      clearTimeout(p.timer);
      p.timer = null;
    }
    if (!lead?.id || p.data == null) return;
    const patch = { application: p.data };
    const nextAmount = Number(p.data?.loanAmount);
    if (Number.isFinite(nextAmount) && nextAmount > 0) {
      patch.estAmount = Math.round(nextAmount);
    }
    // Same first/last → top-level name mirror as the debounced save.
    const fn = (p.data?.firstName || '').trim();
    const ln = (p.data?.lastName  || '').trim();
    if (fn || ln) {
      const fullName = [fn, ln].filter(Boolean).join(' ');
      if (fullName && fullName !== lead.name) {
        patch.name = fullName;
        patch.initials = ((fn[0] || '') + (ln[0] || '')).toUpperCase() || lead.initials;
      }
    }
    p.data = null;
    setSaveStatus('saving');
    try {
      await LeadsStore.update(lead.id, patch);
      setSaveStatus('idle');
      setLastSavedAt(Date.now());
    } catch (_) {
      setSaveStatus('error');
    }
  }, [lead?.id, lead?.name, lead?.initials]);

  // Push the lead to ARIVE via the configured Zapier webhook. Optimistic
  // toast → POST → on success, refresh the lead so the "Pushed to ARIVE"
  // badge appears immediately. Admin-only enforced server-side.
  //
  // CRITICAL: we flush any pending application-form debounce save BEFORE
  // hitting the push endpoint. Otherwise a freshly-edited loanPurpose /
  // propertyType / etc. that's still sitting in the 800ms debounce would
  // never reach Mongo, and ARIVE would receive stale data from the lead doc.
  //
  // Idempotency: server-side 30s cooldown + confirm-on-resend rule prevent
  // accidental duplicate loan creation in ARIVE. UI surfaces this via:
  //   - 429 → toast with countdown message
  //   - 409 → opens the resend confirmation modal
  const performArivePush = useCallback(async ({ confirm = false } = {}) => {
    if (!lead?.id || pushingArive) return;
    setPushingArive(true);
    try {
      await flushPendingSave();
      const r = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/arive/leads/${lead.id}/push`,
        null,
        { params: confirm ? { confirm: 'true' } : undefined },
      );
      toast.success(
        confirm
          ? 'Lead re-sent to ARIVE — note this creates a DUPLICATE loan in ARIVE'
          : 'Lead sent to ARIVE via Zapier',
        { duration: confirm ? 6000 : 4000 },
      );
      try {
        await LeadsStore.patchLead(lead.id, { arivePushedAt: r.data?.pushed_at });
      } catch (_) {}
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.detail || 'Could not send to ARIVE';
      if (status === 429) {
        toast.error(msg, { duration: 6000 });
      } else if (status === 409) {
        // Lead is already in ARIVE — open the resend confirm modal so the
        // LO has to explicitly opt into creating a duplicate loan.
        setResendConfirmOpen(true);
      } else if (status === 400) {
        // Preflight validation error — the message is a bullet-separated
        // list of missing fields (e.g. "Can't push to ARIVE yet — please
        // complete: SSN · DOB · …"). Give the LO enough time to read the
        // full list, and format the middot separators into line breaks
        // so each missing field lands on its own row in the toast.
        const pretty = msg.replace(/ · /g, '\n• ').replace('please complete: ', 'please complete:\n• ');
        toast.error(pretty, { duration: 12000, style: { whiteSpace: 'pre-line' } });
      } else {
        toast.error(msg);
      }
    } finally {
      setPushingArive(false);
    }
  }, [lead?.id, pushingArive, flushPendingSave]);

  const pushToArive = useCallback(async () => {
    // First push fires straight through — no modal needed since there's
    // no duplicate risk. Resend pops the type-to-confirm modal because it
    // creates a duplicate loan in ARIVE.
    if (lead?.arivePushedAt) {
      setResendConfirmOpen(true);
      return;
    }
    await performArivePush();
  }, [lead?.arivePushedAt, performArivePush]);

  // ---- Application form persistence + AI stash ingestion ----
  // We persist the in-progress 1003 to localStorage per-lead so:
  //   (a) page refresh / React remount doesn't wipe the form
  //   (b) AI-extracted fields from the Create-Lead modal land here on first mount
  // Storage key: kf_app_<leadId> = full form data
  // Bridge key:  kf_pending_app_<leadId> = stash written by CreateLeadModal

  useEffect(() => {
    // Hydrate from the FULL lead doc only. Using `fullLead` (strict
    // `_byId[id]`) here is the fix for the "address disappears on refresh"
    // bug — the form needs `application.currentAddress`, SSN, employment,
    // assets, etc. which the LIGHT projection strips out. Without this
    // gate, `setData` would initialize the form from the light cached
    // lead (which has `application.loanPurpose` but no `currentAddress`),
    // and the user's borrower-residence + SSN fields would render blank
    // on every page refresh.
    if (!fullLead) return;
    if (hydratedLeadIdsRef.current.has(fullLead.id)) return;
    hydratedLeadIdsRef.current.add(fullLead.id);

    let stash = null;
    try {
      const raw = localStorage.getItem(`kf_pending_app_${fullLead.id}`);
      if (raw) stash = JSON.parse(raw);
    } catch (_) {}

    // Source of truth ordering (last writer wins):
    //   1. buildInitial(fullLead) — sane defaults derived from the lead doc
    //   2. fullLead.application — server-side persisted 1003 (authoritative)
    //   3. stash (kf_pending_app_<id>) — fresh AI extraction from CreateLeadModal,
    //      consumed once and cleared (a transient unsaved-draft bridge)
    const next = {
      ...buildInitial(fullLead),
      ...(fullLead.application || {}),
      ...(stash || {}),
    };
    setData(next);
    if (stash) {
      try { localStorage.removeItem(`kf_pending_app_${fullLead.id}`); } catch (_) {}
    }
    setHydrated(true);
  }, [fullLead?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-save the form to MongoDB (debounced) so every device sees the same 1003.
  // We deliberately DO NOT keep a localStorage mirror here — the server is the
  // single source of truth and an 800ms debounce keeps unsaved-window tiny.
  //
  // We also mirror the loan amount up to the top-level `estAmount` field so the
  // Pipeline / Leads list / dashboard KPIs reflect edits made inside the 1003.
  //
  // The `pendingSaveRef` and `flushPendingSave` declared above let
  // pushToArive() / unmount cleanup bypass the 800ms wait if the user pushes
  // before the debounce flushes — closes a real data-loss window.
  useEffect(() => {
    if (!hydrated || !lead?.id) return;
    // Skip the no-op save that fires when the user first opens a lead: if
    // the local `data` is identical to the server-side application object,
    // there's nothing to persist. Prevents the "Saved · just now" pill
    // from flashing on every navigation even when no edit happened.
    try {
      const serverJson = JSON.stringify(lead.application || {});
      const localJson = JSON.stringify(data || {});
      if (serverJson === localJson) return;
    } catch (_) { /* fall through and save defensively */ }
    const p = pendingSaveRef.current;
    p.data = data;
    setSaveStatus('pending');
    if (p.timer) clearTimeout(p.timer);
    p.timer = setTimeout(() => {
      const patch = { application: p.data };
      const nextAmount = Number(p.data?.loanAmount);
      if (Number.isFinite(nextAmount) && nextAmount > 0 && nextAmount !== Number(lead.estAmount)) {
        patch.estAmount = Math.round(nextAmount);
      }
      // Mirror firstName/lastName up to the top-level lead.name + initials
      // so the header, avatar, leads list, pipeline cards, etc. stay in
      // sync with edits made inside the 1003. Without this the form would
      // happily save "TESTLEAD TEST SUBJECT" while the header kept showing
      // the original "Michael Hall".
      const fn = (p.data?.firstName || '').trim();
      const ln = (p.data?.lastName  || '').trim();
      if (fn || ln) {
        const fullName = [fn, ln].filter(Boolean).join(' ');
        if (fullName && fullName !== lead.name) {
          patch.name = fullName;
          patch.initials = ((fn[0] || '') + (ln[0] || '')).toUpperCase() || lead.initials;
        }
      }
      const snapshot = p.data;
      setSaveStatus('saving');
      LeadsStore.update(lead.id, patch)
        .then(() => {
          // Only clear the pending flag if no newer keystroke arrived while
          // the request was in flight; otherwise leave the ref alone so the
          // next debounce tick / explicit flush picks up the latest data.
          if (pendingSaveRef.current.data === snapshot) {
            pendingSaveRef.current.data = null;
            setSaveStatus('idle');
            setLastSavedAt(Date.now());
          }
        })
        .catch(() => { setSaveStatus('error'); });
      p.timer = null;
    }, 800);
    return () => {
      if (p.timer) clearTimeout(p.timer);
    };
  }, [data, hydrated, lead?.id, lead?.estAmount, lead?.name, lead?.initials]);

  // Best-effort flush when navigating away — covers the "user changed a
  // field then immediately closed the tab / went to another lead" case.
  useEffect(() => {
    return () => { flushPendingSave(); };
  }, [flushPendingSave]);

  // Co-borrower draft hydration. When the lead first arrives or its id changes,
  // we adopt the server's `co_borrower` object as the local buffer. Once the
  // user starts typing, the buffer leads and the server follows on a debounce.
  // If the server clears the co-borrower (e.g. user removed them), the buffer
  // is cleared too. We deliberately do NOT overwrite the buffer on every server
  // poll — that would clobber in-progress edits.
  const coHydratedRef = useRef(new Set());
  useEffect(() => {
    if (!lead?.id) return;
    const key = `${lead.id}:${lead.co_borrower ? 'on' : 'off'}`;
    if (coHydratedRef.current.has(key)) return;
    coHydratedRef.current.add(key);
    setCoData(lead.co_borrower || null);
  }, [lead?.id, !!lead?.co_borrower]);

  // Debounced co-borrower save (mirrors the 800ms application debounce).
  useEffect(() => {
    if (!hydrated || !lead?.id) return;
    if (coData === null && !lead.co_borrower) return; // nothing to sync
    const handle = setTimeout(() => {
      LeadsStore.update(lead.id, { co_borrower: coData }).catch(() => {});
    }, 800);
    return () => clearTimeout(handle);
  }, [coData, hydrated, lead?.id]);

  // Re-merge incoming lead-doc changes (phone / email / loan amount) into
  // the application form whenever the server-side lead refreshes (polling,
  // Wingman edits, claim toggle, etc.). The initial form state is seeded
  // from `lead.application` in `useState(...)` below — this hook only
  // fires for ROOT-LEVEL fields that the backend mirrors to the lead doc.
  //
  // Note: `currentAddress` (the borrower's street) is NOT re-merged from
  // any root field. It lives solely in `lead.application.currentAddress`
  // and is hydrated by the initial setData. A previous version of this
  // effect mapped `lead.city → currentAddress`, which silently overwrote
  // the street with the city name once the application→root mirror
  // started populating `lead.city`. Fixed by dropping that line.
  useEffect(() => {
    if (!lead || !hydrated) return;
    setData((prev) => ({
      ...prev,
      email: lead.email ?? prev.email,
      phone: lead.phone ?? prev.phone,
      loanAmount: lead.estAmount ?? prev.loanAmount,
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.email, lead?.phone, lead?.estAmount]);

  // Wingman can write to the live application form via wingman:fillApp events
  // and push asset rows via wingman:addAsset.
  useEffect(() => {
    const onFill = (e) => {
      const { field, value } = e?.detail || {};
      if (!field) return;
      setData((prev) => ({ ...prev, [field]: value }));
    };
    const onAddAsset = (e) => {
      const asset = e?.detail?.asset;
      if (!asset || (!asset.institution && !asset.balance)) return;
      setData((prev) => ({ ...prev, assets: [...(prev.assets || []), asset] }));
    };
    window.addEventListener('wingman:fillApp', onFill);
    window.addEventListener('wingman:addAsset', onAddAsset);
    return () => {
      window.removeEventListener('wingman:fillApp', onFill);
      window.removeEventListener('wingman:addAsset', onAddAsset);
    };
  }, []);

  if (!lead || !fullLead) {
    // Three-way render: initial list still loading | on-demand fetch in
    // flight | backend confirmed 404. Only the third renders the empty
    // state. The form below depends on the FULL `application` blob
    // (currentAddress, SSN, employment, income, etc.) which only ships
    // in the GET /leads/{id} response — so even when `useLead` returns
    // the light-projected list-cache version, we still wait for
    // `useFullLead` before mounting the profile.
    if (byIdNotFound) {
      return (
        <div data-testid="lead-not-found" className="max-w-[900px] mx-auto py-12 text-center">
          <div className="display text-[24px] font-bold text-neutral-900">Lead not found</div>
          <Link to="/leads" className="mt-4 inline-flex items-center gap-1.5 text-[12.5px] font-bold text-neutral-900 gold-underline">
            <ArrowLeft className="h-4 w-4" /> Back to Leads
          </Link>
        </div>
      );
    }
    return (
      <div className="max-w-[900px] mx-auto py-20 flex items-center justify-center">
        <div className="text-[12px] text-neutral-500 mono animate-pulse">Loading lead…</div>
      </div>
    );
  }

  // ---- Access gate ---------------------------------------------------
  // Loan officers can only open a lead profile if (a) they have already
  // claimed it OR (b) someone else has — claimed leads are visible to the
  // whole team for collaboration/referral. Unclaimed mailer leads are the
  // private property of the broker shop until an LO takes ownership, so
  // an LO trying to deep-link into one is bounced back to the leads list.
  // Admins always bypass.
  if (!isAdmin && !lead.assignedTo) {
    return (
      <div data-testid="lead-profile-locked" className="max-w-[640px] mx-auto py-16 text-center">
        <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--kf-gold-soft))] text-[hsl(var(--kf-gold-deep))] mb-4">
          <Lock className="h-5 w-5" />
        </div>
        <div className="display text-[22px] font-bold text-neutral-900">Claim this lead first</div>
        <p className="mt-2 text-[13px] text-neutral-500 leading-relaxed">
          This lead is unclaimed and you don't have permission to view their profile yet.
          Head back to search and click <span className="font-semibold text-neutral-700">"Claim lead"</span>
          {' '}to take ownership.
        </p>
        <button
          data-testid="lead-profile-locked-claim"
          onClick={async () => {
            const updated = await LeadsStore.update(lead.id, { assignedTo: user?.id });
            if (updated) {
              toast.success(`${updated.name} is now yours.`);
            } else {
              toast.error('Could not claim this lead');
            }
          }}
          className="mt-6 inline-flex items-center gap-1.5 h-10 px-5 rounded-lg bg-neutral-900 text-[hsl(var(--kf-gold))] text-[13px] font-bold hover:bg-neutral-800 transition"
        >
          <Hand className="h-4 w-4" /> Claim this lead
        </button>
        <div className="mt-4">
          <Link to="/leads" className="inline-flex items-center gap-1.5 text-[12px] font-bold text-neutral-500 hover:text-neutral-900 transition">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Leads
          </Link>
        </div>
      </div>
    );
  }

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const TempIcon = TEMP_ICONS[lead.temp];
  const tempLabel = ALL_TEMPS_BY_KEY[lead.temp]?.label;
  const tempCls = TEMP_STYLES[lead.temp];

  // Live calcs
  const totalIncome = (data.monthlyBaseIncome || 0) + (data.monthlyOvertime || 0) + (data.monthlyBonus || 0) + (data.monthlyCommission || 0) + (data.monthlyOther || 0);
  const totalAssets = (data.assets || []).reduce((s, a) => s + (Number(a.balance) || 0), 0);
  const ltv = data.propertyValue ? Math.round((data.loanAmount / data.propertyValue) * 100) : 0;
  const monthlyTaxes = (data.taxes || 0) / 12;
  const monthlyIns = (data.insurance || 0) / 12;
  const principalInterest = calcMonthly(data.loanAmount, data.rate, data.loanTerm);
  const totalMonthly = principalInterest + monthlyTaxes + monthlyIns + (data.hoa || 0);
  const dti = totalIncome ? Math.round(((totalMonthly + (data.monthlyDebts || 0)) / totalIncome) * 100) : 0;
  const cashToClose = data.loanPurpose === 'Purchase' ? Math.max(0, (data.purchasePrice || 0) - (data.loanAmount || 0)) : 0;

  // Section completion — exclude `summary` (read-only view, no progress)
  const progressSections = SECTIONS.filter((s) => s.key !== 'summary');
  const overallPct = Math.round(
    progressSections.reduce((acc, s) => acc + (completion[s.key] || 0), 0) / progressSections.length
  );

  return (
    <div className="max-w-[1500px] mx-auto space-y-5">
      {/* Header */}
      <div>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="relative">
              <div className="h-14 w-14 rounded-2xl bg-neutral-900 text-white flex items-center justify-center text-[16px] font-bold display">{lead.initials}</div>
              {TempIcon && (
                <div className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full bg-neutral-900 border-2 border-white flex items-center justify-center">
                  <TempIcon className="h-2.5 w-2.5 text-[hsl(var(--kf-gold))]" strokeWidth={3} />
                </div>
              )}
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="display text-[28px] font-bold tracking-tight text-neutral-900">{lead.name}</h1>
                <LoanStatusMenu lead={lead} />
                <AssigneeChip lead={lead} />
                {lead.withdrawn && (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold rounded px-1.5 py-0.5 border bg-neutral-900 text-white border-neutral-900">
                    <Archive className="h-2.5 w-2.5" /> Withdrawn
                  </span>
                )}
                {lead.denied && (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold rounded px-1.5 py-0.5 border bg-red-50 text-red-700 border-red-200" title={`Denied — ${lead.deniedReason || 'reason not set'}`}>
                    <ShieldX className="h-2.5 w-2.5" /> Denied{lead.deniedReason ? ` · ${lead.deniedReason}` : ''}
                  </span>
                )}
                {lead.arivePushedAt && (
                  <span
                    data-testid="arive-pushed-badge"
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold rounded px-1.5 py-0.5 border bg-emerald-50 text-emerald-700 border-emerald-200"
                    title={`Pushed to ARIVE at ${new Date(lead.arivePushedAt).toLocaleString()}`}
                  >
                    <Cloud className="h-2.5 w-2.5" /> In ARIVE
                    {lead.ariveLoanId ? <span className="mono">· {lead.ariveLoanId}</span> : null}
                  </span>
                )}
              </div>
              <p className="text-[12.5px] text-neutral-500 mt-1 flex items-center gap-2 flex-wrap">
                <span>{lead.source} · <span className="mono">{lead.phone}</span> · started 2h ago</span>
                {lead.referenceId && (
                  <button
                    type="button"
                    data-testid="lead-header-reference-id"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(lead.referenceId);
                        toast.success(`Copied ${lead.referenceId}`);
                      } catch {
                        toast.error('Could not copy to clipboard');
                      }
                    }}
                    title="Click to copy reference ID"
                    className="inline-flex items-center gap-1 mono font-bold text-[10.5px] tabular-nums text-neutral-700 bg-neutral-100 hover:bg-neutral-200 border hairline rounded px-1.5 py-0.5 transition"
                  >
                    <Hash className="h-2.5 w-2.5 opacity-60" />
                    {lead.referenceId}
                  </button>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <SaveStatusPill status={saveStatus} lastSavedAt={lastSavedAt} />
            <button
              data-testid="build-from-notes-trigger"
              onClick={() => setBuildOpen(true)}
              className="h-9 px-3 rounded-md btn-gold text-[12px] font-bold flex items-center gap-1.5"
              title="Have Wingman build the application from your saved notes"
            >
              <Wand2 className="h-4 w-4" strokeWidth={2.5} /> Build from notes
            </button>
            <button
              onClick={pushToArive}
              disabled={pushingArive}
              title={lead?.arivePushedAt ? `Last sent ${new Date(lead.arivePushedAt).toLocaleString()}` : 'Push this lead to ARIVE via Zapier'}
              className="h-9 px-4 rounded-md btn-ink text-[12px] font-bold flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="send-to-arive-btn"
            >
              <Send className="h-4 w-4" />
              {pushingArive ? 'Sending…' : (lead?.arivePushedAt ? 'Resend to Arive' : 'Send to Arive')}
            </button>
            <LeadActionsMenu lead={lead} navigate={navigate} pushToArive={pushToArive} pushingArive={pushingArive} isAdmin={isAdmin} />
          </div>
        </div>
      </div>

      {/* Wingman proactive nudge — appears only when notes mention a co-borrower
          and one isn't on the lead yet. Self-dismissing per-lead. */}
      <CoBorrowerNudge lead={lead} />

      {/* Lead status selector — dedicated, one-click. Loan officers status
          their leads here without having to dig into the chip menu. */}
      <StatusBar lead={lead} />

      {/* Stat strip — leaner: no DTI/Monthly P&I (lead-stage estimates only) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Loan amount" value={money(data.loanAmount)} mono />
        <Stat label="Rate" value={`${data.rate}%`} mono />
        <Stat label="LTV" value={`${ltv}%`} mono accent={ltv > 80} />
        <Stat label="Term" value={`${data.loanTerm / 12} yr`} mono />
      </div>

      {/* Application form — full width */}
      <div className="rounded-2xl border hairline bg-white overflow-hidden">
        {/* Tabs — kept on a single line. Inactive tabs show only the label
            + a quiet status dot (gold = complete, neutral hollow = partial,
            hidden if untouched). Only the active tab carries the % chip so
            the row stays readable without wrapping. */}
        <div className="border-b hairline bg-neutral-50/40 px-3 py-2 flex items-center gap-x-1 gap-y-1.5 overflow-x-auto">
          <div className="flex items-center gap-0.5 flex-1 min-w-0 whitespace-nowrap">
            {SECTIONS.map((s) => {
              const Icon = s.icon;
              const active = section === s.key;
              const pct = completion[s.key] || 0;
              const done = pct === 100;
              const showProgress = s.key !== 'summary';
              return (
                <button
                  key={s.key}
                  onClick={() => setSection(s.key)}
                  data-testid={`section-tab-${s.key}`}
                  className={cn(
                    'h-9 flex items-center gap-1.5 px-2.5 rounded-md text-[12.5px] font-semibold transition whitespace-nowrap relative shrink-0',
                    active ? 'bg-white border hairline text-neutral-900 shadow-sm' : 'text-neutral-500 hover:text-neutral-900'
                  )}
                >
                  <Icon className={cn('h-3.5 w-3.5', active ? 'text-neutral-900' : 'text-neutral-400')} />
                  {s.label}
                  {showProgress && (
                    active
                      ? (
                        <span className={cn(
                          'rounded-full text-[10px] font-bold px-1.5 py-0.5 leading-none border tabular-nums',
                          done
                            ? 'bg-[hsl(var(--kf-gold-soft))] text-[hsl(var(--kf-gold-deep))] border-[hsl(var(--kf-gold))]/40'
                            : 'bg-neutral-900 text-[hsl(var(--kf-gold))] border-neutral-900',
                        )}>
                          {pct}%
                        </span>
                      )
                      : done
                        ? <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--kf-gold))]" />
                        : pct > 0
                          ? <span className="h-1.5 w-1.5 rounded-full border border-neutral-400 bg-white" />
                          : null
                  )}
                </button>
              );
            })}
          </div>
          {/* Compact progress indicator — matched to h-9 tab height. */}
          <div className="flex items-center gap-2 pl-3 border-l hairline ml-1 shrink-0 h-9">
            <div className="text-right">
              <div className="text-[9.5px] uppercase tracking-wider text-neutral-500 font-bold leading-none">Progress</div>
              <div className="text-[13px] font-bold text-neutral-900 leading-tight mt-0.5 tabular-nums">{overallPct}%</div>
            </div>
            <div className="relative h-8 w-8 rounded-full flex items-center justify-center"
              style={{ background: `conic-gradient(hsl(var(--kf-gold)) ${overallPct}%, hsl(var(--kf-line)) 0)` }}>
              <div className="h-6 w-6 rounded-full bg-[hsl(var(--kf-surface))]" />
            </div>
          </div>
        </div>

        <div className="px-6 lg:px-8 pt-5 pb-6 lg:pt-6 lg:pb-8">
          {section === 'profile' && <ProfileTab lead={lead} data={data} set={set} setMany={(patch) => setData((d) => ({ ...d, ...patch }))} flushSave={flushPendingSave} coData={coData} setCoData={setCoData} />}
          {section === 'employment' && <EmploymentTab lead={lead} data={data} set={set} totalIncome={totalIncome} coData={coData} setCoData={setCoData} />}
          {section === 'assets' && <AssetsSection data={data} set={set} totalAssets={totalAssets} />}
          {section === 'liabilities' && <LiabilitiesTab lead={lead} data={data} set={set} coData={coData} setCoData={setCoData} />}
          {section === 'property' && <PropertySection data={data} set={set} setMany={(patch) => setData((d) => ({ ...d, ...patch }))} flushSave={flushPendingSave} />}
          {section === 'loan' && <LoanSection data={data} set={set} />}
          {section === 'notes' && (
            <div className="space-y-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="display text-[16px] font-bold text-neutral-900 tracking-tight">Lead notes & follow-up</h3>
                <LeadCreatedChip createdAt={lead.created_at} />
              </div>
              <AppointmentScheduler lead={lead} />
              <NotePad leadId={lead.id} title={`Notes on ${lead.name}`} subtitle="Anything you want to remember — call recaps, borrower preferences, paperwork blockers." />
            </div>
          )}
          {section === 'summary' && <LoanSummaryView lead={lead} data={data} />}
        </div>

        {/* Section nav footer */}
        <div className="px-6 lg:px-8 py-4 border-t hairline bg-neutral-50/40 flex items-center justify-between">
          <button
            onClick={() => {
              const i = SECTIONS.findIndex((s) => s.key === section);
              if (i > 0) setSection(SECTIONS[i - 1].key);
            }}
            disabled={SECTIONS.findIndex((s) => s.key === section) === 0}
            className="h-9 px-3 rounded-md border hairline bg-white hover:border-neutral-400 text-[12px] font-semibold text-neutral-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Previous section
          </button>
          <div className="text-[11px] text-neutral-500">
            Section {SECTIONS.findIndex((s) => s.key === section) + 1} of {SECTIONS.length}
          </div>
          <button
            onClick={() => {
              const i = SECTIONS.findIndex((s) => s.key === section);
              if (i < SECTIONS.length - 1) setSection(SECTIONS[i + 1].key);
              else pushToArive();
            }}
            disabled={pushingArive && SECTIONS.findIndex((s) => s.key === section) === SECTIONS.length - 1}
            className="h-9 px-3 rounded-md btn-ink text-[12px] font-bold flex items-center gap-1.5 disabled:opacity-50"
          >
            {SECTIONS.findIndex((s) => s.key === section) === SECTIONS.length - 1 ? (
              <>Send to Arive <ArrowUpRight className="h-3.5 w-3.5" /></>
            ) : (
              <>Next section <ArrowUpRight className="h-3.5 w-3.5" /></>
            )}
          </button>
        </div>
      </div>

      <BuildFromNotesModal
        open={buildOpen}
        leadId={lead.id}
        leadName={lead.name}
        onClose={() => setBuildOpen(false)}
        onLeadPatch={async (patch) => {
          // Apply lead-level patch via leadsStore (persists to /api/leads PATCH)
          if (lead?.id && patch && Object.keys(patch).length) {
            await LeadsStore.update(lead.id, patch);
          }
        }}
      />

      <ResendToAriveModal
        open={resendConfirmOpen}
        leadName={lead.name}
        lastPushedAt={lead.arivePushedAt}
        pushing={pushingArive}
        onClose={() => setResendConfirmOpen(false)}
        onConfirm={async () => {
          setResendConfirmOpen(false);
          await performArivePush({ confirm: true });
        }}
      />
    </div>
  );
}

/* =============== Sections =============== */

function ProfileTab({ lead, data, set, setMany, flushSave, coData, setCoData }) {
  const hasCo = !!lead?.co_borrower;
  const [layout, setLayout] = useState(() => localStorage.getItem('kf_coborrower_layout') || 'stacked');

  const setLayoutPersist = (v) => {
    setLayout(v);
    localStorage.setItem('kf_coborrower_layout', v);
    window.dispatchEvent(new CustomEvent('kf:layout-change', { detail: v }));
  };

  const toggleCoBorrower = async () => {
    if (hasCo) {
      if (!window.confirm('Remove the co-borrower from this lead? Their information will be deleted.')) return;
      const updated = await LeadsStore.update(lead.id, { co_borrower: null });
      if (updated) {
        setCoData(null);
        toast.success('Co-borrower removed');
      }
    } else {
      const seed = { firstName: '', lastName: '', dob: '', ssn: '', phone: '', email: '', marital: 'Married', citizenship: 'US Citizen' };
      const updated = await LeadsStore.update(lead.id, { co_borrower: seed });
      if (updated) {
        setCoData(seed);
        toast.success('Co-borrower added');
      }
    }
  };

  // Local-only co-borrower edits — the debounced effect in the parent
  // LeadProfile pushes the buffer to MongoDB. This eliminates the
  // per-keystroke server round-trip that produced the typing "glitch".
  const updateCoField = (field, value) => {
    setCoData((prev) => ({ ...(prev || {}), [field]: value }));
  };
  const updateCoMany = (patch) => {
    setCoData((prev) => ({ ...(prev || {}), ...patch }));
  };

  const primaryCard = (
    <section className="rounded-2xl border hairline bg-white p-6 md:p-7">
      <div className="flex items-center gap-3 mb-6 pb-4 border-b hairline-soft">
        <div className="h-10 w-10 rounded-full bg-neutral-900 text-[hsl(var(--kf-gold))] flex items-center justify-center">
          <User2 className="h-4 w-4" strokeWidth={2.5} />
        </div>
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-neutral-500 font-bold">Primary borrower</div>
          <div className="display text-[17px] font-bold text-neutral-900 tracking-tight">
            {data.firstName} {data.lastName}
          </div>
        </div>
      </div>
      <ProfileSection data={data} set={set} setMany={setMany} flushSave={flushSave} dense={layout === 'split'} />
    </section>
  );

  const coCard = hasCo && (
    <section className="rounded-2xl border hairline bg-white p-6 md:p-7 rise-in">
      <div className="flex items-center gap-3 mb-6 pb-4 border-b hairline-soft">
        <div className="h-10 w-10 rounded-full bg-[hsl(var(--kf-gold-soft))] text-[hsl(var(--kf-gold-deep))] flex items-center justify-center border border-[hsl(var(--kf-gold))]/30">
          <User2 className="h-4 w-4" strokeWidth={2.5} />
        </div>
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-[hsl(var(--kf-gold-deep))] font-bold">Co-borrower</div>
          <div className="display text-[17px] font-bold text-neutral-900 tracking-tight">
            {coData?.firstName || '—'} {coData?.lastName || ''}
          </div>
        </div>
      </div>
      <CoBorrowerSection
        primary={data}
        data={coData || {}}
        set={updateCoField}
        setMany={updateCoMany}
        dense={layout === 'split'}
      />
    </section>
  );

  return (
    <div className="space-y-6">
      {/* Toolbar: co-borrower toggle + (when on) layout toggle */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-white rounded-xl border hairline px-4 py-3">
        <button
          data-testid="coborrower-toggle"
          onClick={toggleCoBorrower}
          className="flex items-center gap-3 group"
        >
          <span className={cn(
            'relative inline-flex h-6 w-11 rounded-full transition shrink-0',
            hasCo ? 'bg-neutral-900' : 'bg-neutral-200'
          )}>
            <span className={cn(
              'absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition',
              hasCo ? 'left-[22px]' : 'left-0.5'
            )} />
          </span>
          <span className="text-[13px] font-bold text-neutral-900">
            {hasCo ? 'Co-borrower on' : 'Add co-borrower'}
          </span>
          {hasCo && (
            <span className="text-[11.5px] text-neutral-500">
              — two borrowers on this application
            </span>
          )}
        </button>

        {hasCo && (
          <div className="flex items-center gap-2">
            <span className="text-[10.5px] uppercase tracking-[0.18em] text-neutral-400 font-semibold">Layout</span>
            <div data-testid="layout-toggle" className="inline-flex items-center gap-0.5 rounded-lg border hairline p-0.5 bg-neutral-50">
              <button
                data-testid="layout-stacked"
                onClick={() => setLayoutPersist('stacked')}
                className={cn(
                  'h-7 px-2.5 rounded-md text-[11.5px] font-semibold transition flex items-center gap-1.5',
                  layout === 'stacked' ? 'bg-neutral-900 text-white' : 'text-neutral-600 hover:text-neutral-900'
                )}
                title="Stack primary above co-borrower"
              >
                <Rows3 className="h-3.5 w-3.5" /> Stacked
              </button>
              <button
                data-testid="layout-split"
                onClick={() => setLayoutPersist('split')}
                className={cn(
                  'h-7 px-2.5 rounded-md text-[11.5px] font-semibold transition flex items-center gap-1.5',
                  layout === 'split' ? 'bg-neutral-900 text-white' : 'text-neutral-600 hover:text-neutral-900'
                )}
                title="Side-by-side primary and co-borrower"
              >
                <Columns2 className="h-3.5 w-3.5" /> Split
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Layout */}
      {hasCo && layout === 'split' ? (
        <div className="grid grid-cols-1 2xl:grid-cols-2 gap-6">
          {primaryCard}
          {coCard}
        </div>
      ) : (
        <div className="space-y-6">
          {primaryCard}
          {coCard}
        </div>
      )}
    </div>
  );
}

function ProgressCard({ completion, overallPct, onJump }) {
  return (
    <div className="rounded-2xl border hairline bg-white p-6">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-neutral-500 font-bold">Application progress</div>
          <div className="display text-[20px] font-bold text-neutral-900 tracking-tight">{overallPct}% complete</div>
        </div>
        <div className="relative h-12 w-12 rounded-full flex items-center justify-center"
          style={{ background: `conic-gradient(hsl(var(--kf-gold)) ${overallPct}%, hsl(0 0% 92%) 0)` }}>
          <div className="h-9 w-9 rounded-full bg-white flex items-center justify-center mono text-[10px] font-bold text-neutral-900">{overallPct}%</div>
        </div>
      </div>
      <div className="space-y-1.5">
        {SECTIONS.map((s) => {
          const pct = completion[s.key] || 0;
          return (
            <button key={s.key} onClick={() => onJump(s.key)}
              className="w-full flex items-center gap-3 px-2 py-1.5 rounded-md hover:bg-neutral-50">
              <s.icon className="h-3.5 w-3.5 text-neutral-500" />
              <span className="text-[12px] font-semibold text-neutral-800 flex-1 text-left">{s.label}</span>
              <span className="w-16 h-1 rounded-full bg-neutral-100 overflow-hidden">
                <span className={cn('block h-full rounded-full', pct === 100 ? 'bg-[hsl(var(--kf-gold))]' : 'bg-neutral-900')} style={{ width: `${pct}%` }} />
              </span>
              <span className="mono text-[10.5px] text-neutral-500 w-8 text-right">{pct}%</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Status bar — dedicated, prominent row of one-click status pills.   */
/* Sits right under the header so the LO can tag the lead without     */
/* having to open the small chip menu in the title row.               */
/* ------------------------------------------------------------------ */

/* SourcePicker — replaces the static "Tag where this lead sits in     */
/* your pipeline" subtitle with the actual lead source, clickable to   */
/* change. Normalizes legacy stored values onto the canonical list.    */
function SourcePicker({ lead }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = normalizeSource(lead.source);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const pick = async (src) => {
    setOpen(false);
    if (src === lead.source) return;
    const updated = await LeadsStore.update(lead.id, { source: src });
    if (updated) toast.success(`${updated.name} → source: ${src}`);
    else toast.error('Could not update source');
  };

  return (
    <div className="relative mt-1" ref={ref}>
      <button
        type="button"
        data-testid="lead-source-trigger"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 text-[12px] font-semibold text-neutral-800 hover:text-neutral-900 transition group"
        title="Change lead source"
      >
        <span className="text-neutral-500 text-[11px] mr-0.5">Source:</span>
        <span className="underline-offset-4 group-hover:underline decoration-[hsl(var(--kf-gold))]">
          {current || 'Set source'}
        </span>
        <ChevronDown className="h-3 w-3 opacity-60 group-hover:opacity-100" />
      </button>
      {open && (
        <div
          data-testid="lead-source-menu"
          className="absolute z-30 top-full mt-1.5 left-0 w-[240px] rounded-xl border hairline bg-white shadow-[0_18px_48px_-18px_rgba(0,0,0,0.25)] overflow-hidden"
        >
          <div className="px-3 py-2 text-[9.5px] uppercase tracking-wider font-bold text-neutral-400 border-b hairline-soft">Lead Source</div>
          {LEAD_SOURCES.map((src) => {
            const active = src === current;
            return (
              <button
                key={src}
                data-testid={`lead-source-option-${src.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => pick(src)}
                className={cn(
                  'w-full flex items-center gap-2 px-3 py-2 text-[12.5px] font-semibold transition text-left',
                  active ? 'bg-neutral-50 text-neutral-900' : 'text-neutral-700 hover:bg-neutral-100'
                )}
              >
                <span className="flex-1">{src}</span>
                {active && <Check className="h-3.5 w-3.5 text-[hsl(var(--kf-gold-deep))]" strokeWidth={3} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StatusBar({ lead }) {
  // Show only the active LO-facing pipeline statuses here. Legacy
  // statuses (follow_up / nurture / dnc / dead) still display on the
  // header chip if present on the record but aren't selectable from
  // the picker — DNC / Withdraw / Deny / Reinstate live in the `…`
  // actions menu since they're destructive or workflow-final.
  const PIPELINE_STATUSES = leadTemps.map((t) => t.key);
  const pick = async (key) => {
    // Toggle-off: clicking the currently-active chip clears it (no status).
    // Explicit `null` from the "No Status" button always clears.
    const next = key === null ? null : (key === lead.temp ? null : key);
    if (next === (lead.temp || null)) return;
    const updated = await LeadsStore.update(lead.id, { temp: next });
    if (updated) {
      const label = next === null ? 'No status' : (ALL_TEMPS_BY_KEY[next]?.label || next);
      toast.success(`${updated.name} → ${label}`);
    } else {
      toast.error('Could not update status');
    }
  };

  const hasTemp = !!lead.temp;

  return (
    <div
      data-testid="lead-statusbar"
      className="flex flex-wrap items-center gap-1.5"
    >
      {/* Explicit "No Status" — clears the lead.temp value. Renders first
          so it reads as the "default" position in the status spectrum. */}
      <button
        data-testid="lead-statusbar-none"
        onClick={() => pick(null)}
        title="Clear lead status"
        className={cn(
          'inline-flex items-center gap-1.5 h-9 px-3 rounded-full border text-[12px] font-bold transition-all',
          !hasTemp
            ? 'bg-neutral-900 text-white border-neutral-900 shadow-[0_4px_14px_-6px_rgba(0,0,0,0.25)] scale-[1.02]'
            : 'bg-white text-neutral-700 border-neutral-200 hover:bg-neutral-900 hover:border-neutral-900 hover:text-[hsl(var(--kf-gold))] hover:shadow-[0_4px_14px_-6px_rgba(0,0,0,0.35)]'
        )}
      >
        <X className="h-3 w-3" strokeWidth={2.5} />
        No Status
      </button>
      {PIPELINE_STATUSES.map((key) => {
        const t = leadTemps.find((x) => x.key === key);
        if (!t) return null;
        const Icon = TEMP_ICONS[key];
        const styles = TEMP_STYLES[key] || TEMP_STYLES.nurture;
        const active = lead.temp === key;
        return (
          <button
            key={key}
            data-testid={`lead-statusbar-${key}`}
            onClick={() => pick(key)}
            title={`Set status: ${t.label}`}
            className={cn(
              'inline-flex items-center gap-1.5 h-9 px-3 rounded-full border text-[12px] font-bold transition-all',
              active
                ? cn(styles.chip, 'shadow-[0_4px_14px_-6px_rgba(0,0,0,0.25)] scale-[1.02]')
                : 'bg-white text-neutral-800 border-neutral-200 hover:bg-neutral-900 hover:border-neutral-900 hover:text-[hsl(var(--kf-gold))] hover:shadow-[0_4px_14px_-6px_rgba(0,0,0,0.35)]'
            )}
          >
            <Icon className="h-3 w-3" strokeWidth={2.5} />
            {t.label}
          </button>
        );
      })}
    </div>
  );
}


/* ------------------------------------------------------------------ */
/* Assignee — compact header chip + reusable Reassign dialog.         */
/* The chip shows who's responsible at a glance. The full picker is   */
/* triggered from the `…` actions menu via ReassignDialog.            */
/* ------------------------------------------------------------------ */
const AVATAR_TONES = {
  ink:    { bg: 'bg-neutral-900',           text: 'text-white' },
  gold:   { bg: 'bg-[hsl(var(--kf-gold))]', text: 'text-neutral-900' },
  indigo: { bg: 'bg-indigo-600',            text: 'text-white' },
  rose:   { bg: 'bg-rose-500',              text: 'text-white' },
  neutral:{ bg: 'bg-neutral-200',           text: 'text-neutral-700' },
};

function LoAvatar({ member, size = 'md' }) {
  if (!member) {
    return (
      <span className={cn(
        'shrink-0 rounded-full bg-neutral-200 text-neutral-500 flex items-center justify-center font-bold',
        size === 'xs' ? 'h-5 w-5 text-[9px]' :
        size === 'sm' ? 'h-7 w-7 text-[10px]' : 'h-10 w-10 text-[12px]'
      )}>?</span>
    );
  }
  const tone = AVATAR_TONES[member.avatarTone] || AVATAR_TONES.neutral;
  return (
    <span className={cn(
      'shrink-0 rounded-full flex items-center justify-center font-bold',
      tone.bg, tone.text,
      size === 'xs' ? 'h-5 w-5 text-[9px]' :
      size === 'sm' ? 'h-7 w-7 text-[10px]' : 'h-10 w-10 text-[12px]'
    )}>
      {member.initials || (member.name || '?').slice(0, 2).toUpperCase()}
    </span>
  );
}

function AssigneeChip({ lead }) {
  const team = useTeam();
  const [open, setOpen] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const ref = useRef(null);
  const assignedId = lead.assignedTo || null;
  const assignee = assignedId ? (team.find((m) => m.id === assignedId) || TeamStore.get(assignedId)) : null;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <span className="relative inline-flex" ref={ref}>
      <button
        type="button"
        data-testid="lead-assignee-chip"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'inline-flex items-center gap-1.5 text-[12.5px] font-bold rounded-full pl-1 pr-3 py-1 border hairline transition-colors',
          open
            ? 'bg-neutral-900 text-white border-neutral-900'
            : 'bg-white text-neutral-700 hover:border-neutral-400'
        )}
        title={assignee ? assignee.name : 'Unassigned'}
      >
        <LoAvatar member={assignee} size="xs" />
        <span className="truncate max-w-[140px]" data-testid="lead-assignee-name">
          {assignee?.name?.split(' ')[0] || 'Unassigned'}
        </span>
        {assignee?.role === 'admin' && (
          <Crown className={cn('h-3 w-3', open ? 'text-[hsl(var(--kf-gold))]' : 'text-[hsl(var(--kf-gold-deep))]')} strokeWidth={2.5} />
        )}
      </button>

      {open && (
        <div
          data-testid="lead-assignee-popover"
          className="absolute z-30 top-full left-0 mt-2 w-[260px] rounded-xl border hairline bg-white shadow-[0_18px_48px_-18px_rgba(0,0,0,0.25)] overflow-hidden"
        >
          {assignee ? (
            <>
              <div className="px-3 pt-3 pb-2.5 flex items-start gap-2.5">
                <LoAvatar member={assignee} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[13px] font-bold text-neutral-900 truncate" data-testid="lead-assignee-fullname">{assignee.name}</span>
                    {assignee.role === 'admin' && (
                      <Crown className="h-3 w-3 text-[hsl(var(--kf-gold-deep))] shrink-0" strokeWidth={2.5} />
                    )}
                  </div>
                  {assignee.title && (
                    <div className="text-[10.5px] text-neutral-500 mt-0.5">{assignee.title}</div>
                  )}
                  {assignee.email && (
                    <a href={`mailto:${assignee.email}`} className="block text-[10.5px] text-[hsl(var(--kf-gold-deep))] hover:underline truncate mt-0.5">{assignee.email}</a>
                  )}
                </div>
              </div>
              <button
                data-testid="lead-assignee-popover-reassign"
                onClick={() => { setOpen(false); setReassignOpen(true); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-[12px] font-semibold text-neutral-800 hover:bg-neutral-50 transition border-t hairline-soft"
              >
                <Shuffle className="h-3.5 w-3.5 text-neutral-700" />
                Reassign loan officer
              </button>
            </>
          ) : (
            <div className="px-3 py-3 text-[12px] text-neutral-500 italic">Unassigned — pick a loan officer below.</div>
          )}
        </div>
      )}

      {reassignOpen && <ReassignDialog lead={lead} onClose={() => setReassignOpen(false)} />}
    </span>
  );
}

function ReassignDialog({ lead, onClose }) {
  const team = useTeam();
  const [query, setQuery] = useState('');
  const assignedId = lead.assignedTo || null;

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return team
      .filter((m) => m.active !== false)
      .filter((m) => !q || m.name.toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q));
  }, [team, query]);

  const reassign = async (member) => {
    onClose();
    if (member.id === assignedId) return;
    const updated = await LeadsStore.update(lead.id, { assignedTo: member.id });
    if (updated) toast.success(`${lead.name} reassigned to ${member.name}`);
    else toast.error('Could not reassign');
  };

  // Render a tiny centered modal via portal so click-outside on the
  // backdrop closes it cleanly (and the modal doesn't get clipped by the
  // ancestor card overflow).
  return createPortal(
    <div
      data-testid="reassign-dialog"
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fadeUp"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-[420px] max-w-full rounded-2xl border hairline bg-white shadow-[0_40px_120px_-30px_rgba(0,0,0,0.4)] overflow-hidden">
        <div className="px-4 py-3 border-b hairline-soft flex items-center justify-between">
          <div>
            <div className="text-[12.5px] font-bold text-neutral-900">Reassign loan officer</div>
            <div className="text-[10.5px] text-neutral-500 mt-0.5">Pick who should own {lead.name} going forward.</div>
          </div>
          <button onClick={onClose} className="h-7 w-7 rounded-md hover:bg-neutral-100 flex items-center justify-center text-neutral-500">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-4 py-3 border-b hairline-soft">
          <input
            autoFocus
            data-testid="lead-reassign-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search loan officer…"
            className="w-full h-9 px-3 text-[12.5px] bg-neutral-50 rounded-md border hairline focus:outline-none focus:border-neutral-900"
          />
        </div>
        <div className="max-h-[360px] overflow-y-auto">
          {filtered.length === 0 && (
            <div className="px-3 py-6 text-[12px] text-neutral-400 italic text-center">No matches.</div>
          )}
          {filtered.map((m) => {
            const active = m.id === assignedId;
            return (
              <button
                key={m.id}
                data-testid={`lead-reassign-option-${m.id}`}
                onClick={() => reassign(m)}
                className={cn(
                  'w-full flex items-center gap-3 px-4 py-2.5 text-left transition',
                  active ? 'bg-neutral-50' : 'hover:bg-neutral-50'
                )}
              >
                <LoAvatar member={m} size="sm" />
                <span className="flex-1 min-w-0">
                  <span className="flex items-center gap-1">
                    <span className="text-[12.5px] font-bold text-neutral-900 truncate">{m.name}</span>
                    {m.role === 'admin' && <Crown className="h-2.5 w-2.5 text-[hsl(var(--kf-gold-deep))]" />}
                  </span>
                  <span className="block text-[10.5px] text-neutral-500 truncate">
                    {m.title || (m.role === 'admin' ? 'Admin' : 'Loan Officer')}
                  </span>
                </span>
                {active && <Check className="h-3.5 w-3.5 text-[hsl(var(--kf-gold-deep))]" strokeWidth={3} />}
              </button>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
}



/* ------------------------------------------------------------------ */
/* Status (temperature) menu — click the chip to change lead status   */
/* ------------------------------------------------------------------ */
function StatusMenu({ lead }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const hasTemp = !!lead.temp;
  const TempIcon = hasTemp ? (TEMP_ICONS[lead.temp] || Flame) : X;
  const tempLabel = hasTemp ? (ALL_TEMPS_BY_KEY[lead.temp]?.label || 'Status') : 'No status';
  const cls = hasTemp ? (TEMP_STYLES[lead.temp] || TEMP_STYLES.warm) : 'bg-white text-neutral-500 border-neutral-200 hover:text-neutral-900';

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const change = async (key) => {
    setOpen(false);
    // Toggle-off: clicking the currently-active status clears it back to null.
    const next = key === lead.temp ? null : key;
    if (next === lead.temp) return;
    const updated = await LeadsStore.update(lead.id, { temp: next });
    if (updated) {
      const lbl = next === null ? 'No status' : (ALL_TEMPS_BY_KEY[next]?.label || next);
      toast.success(`${updated.name} → ${lbl}`);
    } else {
      toast.error('Could not update status');
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid="lead-status-trigger"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'inline-flex items-center gap-1.5 text-[11.5px] uppercase tracking-wider font-bold rounded-md px-2 py-1 border transition hover:scale-[1.03]',
          cls
        )}
        title="Change lead status"
      >
        <TempIcon className="h-3 w-3" /> {tempLabel}
        <ChevronDown className="h-3 w-3 opacity-70" />
      </button>
      {open && (
        <div
          data-testid="lead-status-menu"
          className="absolute z-30 top-full mt-1.5 left-0 w-[220px] rounded-xl border hairline bg-white shadow-[0_18px_48px_-18px_rgba(0,0,0,0.25)] overflow-hidden"
        >
          <div className="px-3 py-2 text-[9.5px] uppercase tracking-wider font-bold text-neutral-400 border-b hairline-soft">Lead Status</div>
          {leadTemps.map((t) => {
            const Icon = TEMP_ICONS[t.key];
            const active = t.key === lead.temp;
            return (
              <button
                key={t.key}
                data-testid={`lead-status-option-${t.key}`}
                onClick={() => change(t.key)}
                className={cn(
                  'w-full flex items-center gap-2 px-3 py-2 text-[12.5px] font-semibold transition text-left',
                  active ? 'bg-neutral-50 text-neutral-900' : 'text-neutral-700 hover:bg-neutral-50'
                )}
              >
                <Icon className="h-3.5 w-3.5 shrink-0" />
                <span className="flex-1">{t.label}</span>
                {active && <Check className="h-3.5 w-3.5 text-[hsl(var(--kf-gold-deep))]" strokeWidth={3} />}
              </button>
            );
          })}
          <div className="border-t hairline-soft" />
          <button
            data-testid="lead-status-option-clear"
            onClick={() => change(null)}
            className={cn(
              'w-full flex items-center gap-2 px-3 py-2 text-[12.5px] font-semibold transition text-left',
              !hasTemp ? 'bg-neutral-50 text-neutral-900' : 'text-neutral-500 hover:bg-neutral-50 hover:text-neutral-900'
            )}
            title="Remove the status from this lead — it stays in your pipeline without a tag."
          >
            <X className="h-3.5 w-3.5 shrink-0" />
            <span className="flex-1">No status</span>
            {!hasTemp && <Check className="h-3.5 w-3.5 text-[hsl(var(--kf-gold-deep))]" strokeWidth={3} />}
          </button>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Loan Status Menu — drives mailer dashboard's submitted / funded     */
/* funnel. Separate from `temp` (lead disposition) so a hot lead can   */
/* still be "conditionally approved" simultaneously.                    */
/* ------------------------------------------------------------------ */
const LOAN_STATUSES = [
  { key: 'ready_to_submit',           label: 'Ready to Submit',        Icon: ClipboardList,  tone: 'bg-neutral-100 text-neutral-700 border-neutral-200' },
  { key: 'submitted_to_uw',           label: 'Submitted to UW',        Icon: SendIcon,       tone: 'bg-blue-50 text-blue-700 border-blue-200' },
  { key: 'approved_with_conditions',  label: 'Approved w/ Conditions', Icon: FileCheck,      tone: 'bg-violet-50 text-violet-700 border-violet-200' },
  { key: 'clear_to_close',            label: 'Clear to Close',         Icon: FileSignature,  tone: 'bg-orange-50 text-orange-700 border-orange-200' },
  { key: 'funded',                    label: 'Funded',                 Icon: Trophy,         tone: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
];
// Legacy keys carried in from previous taxonomies still render nicely by
// aliasing to the closest equivalent in the current 5-stage set.
const LOAN_STATUS_LEGACY = {
  submitted: 'submitted_to_uw',
  conditionally_approved: 'approved_with_conditions',
  setup: 'ready_to_submit',
  application: 'submitted_to_uw',
  processing: 'submitted_to_uw',
  underwriting: 'submitted_to_uw',
  conditional_approval: 'approved_with_conditions',
  cd_docs_out: 'clear_to_close',
};
const LOAN_STATUS_BY_KEY = Object.fromEntries(LOAN_STATUSES.map((s) => [s.key, s]));

function LoanStatusMenu({ lead }) {
  const [open, setOpen] = useState(false);
  const [showReadyModal, setShowReadyModal] = useState(false);
  const [showFundedModal, setShowFundedModal] = useState(false);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const stageKey = lead.loanStatus ? (LOAN_STATUS_LEGACY[lead.loanStatus] || lead.loanStatus) : null;
  const current = stageKey ? LOAN_STATUS_BY_KEY[stageKey] : null;
  const Icon = current?.Icon || FileSignature;
  const label = current?.label || 'Loan stage';
  const cls = current?.tone || 'bg-white text-neutral-500 border-neutral-200 hover:text-neutral-900';

  // Compute menu position on open + on scroll/resize. Portalled + fixed
  // so the menu can never be clipped by an ancestor's overflow rect (the
  // Lead Profile header sits inside a card with `overflow-hidden` that
  // was cutting the dropdown off before this fix).
  const recomputePos = React.useCallback(() => {
    const btn = triggerRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const MENU_H = LOAN_STATUSES.length * 40 + 96; // rows + header + clear + padding
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < MENU_H + 16 && rect.top > MENU_H;
    setPos({
      top: openUp ? Math.max(8, rect.top - MENU_H - 6) : rect.bottom + 6,
      left: Math.max(8, Math.min(rect.left, window.innerWidth - 256)),
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    recomputePos();
    const onClick = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      if (triggerRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onScrollOrResize = () => recomputePos();
    document.addEventListener('mousedown', onClick);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onClick);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, recomputePos]);

  // True when the deal already has every field the Pipeline needs to be
  // useful. Drives whether the ready-to-submit modal is required.
  const lender = (lead.application?.lender || lead.lender || '').trim();
  const loanNumber = (lead.loanNumber || '').trim();
  const hasAllPipelineFields = !!(lender && loanNumber && lead.estimatedClosingDate && lead.loanChannel);

  const change = async (key) => {
    setOpen(false);
    const next = key === lead.loanStatus ? null : key;
    if (next === lead.loanStatus) return;
    // Promotion to Ready to Submit is the moment the deal leaves /leads
    // and lands in /pipeline. Capture the pipeline-critical metadata
    // (lender, loan #, close date, channel) up front so the row arrives
    // in Pipeline fully populated rather than half-blank.
    if (next === 'ready_to_submit' && !hasAllPipelineFields) {
      setShowReadyModal(true);
      return;
    }
    // Funded is a one-way trapdoor into the Fundings ledger — confirm
    // the closing date first so monthly production is stamped right.
    if (next === 'funded') {
      setShowFundedModal(true);
      return;
    }
    const updated = await LeadsStore.update(lead.id, { loanStatus: next });
    if (updated) {
      const lbl = next ? LOAN_STATUS_BY_KEY[next].label : 'Cleared';
      toast.success(`Loan stage → ${lbl}`);
    } else {
      toast.error('Could not update loan stage');
    }
  };

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        data-testid="lead-loanstatus-trigger"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'inline-flex items-center gap-1.5 text-[11.5px] uppercase tracking-wider font-bold rounded-md px-2 py-1 border transition hover:scale-[1.03]',
          cls,
        )}
        title="Change loan stage (mailer reporting funnel)"
      >
        <Icon className="h-3 w-3" /> {label}
        <ChevronDown className="h-3 w-3 opacity-70" />
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          data-testid="lead-loanstatus-menu"
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: 240, zIndex: 9999 }}
          className="rounded-xl border hairline bg-white shadow-[0_18px_48px_-18px_rgba(0,0,0,0.25)] overflow-hidden"
        >
          <div className="px-3 py-2 text-[9.5px] uppercase tracking-wider font-bold text-neutral-400 border-b hairline-soft">Loan Stage</div>
          {LOAN_STATUSES.map((s) => {
            const active = s.key === stageKey;
            const ItemIcon = s.Icon;
            return (
              <button
                key={s.key}
                data-testid={`lead-loanstatus-option-${s.key}`}
                onClick={() => change(s.key)}
                className={cn(
                  'w-full flex items-center gap-2 px-3 py-2 text-[12.5px] font-semibold transition text-left',
                  active ? 'bg-neutral-50 text-neutral-900' : 'text-neutral-700 hover:bg-neutral-50'
                )}
              >
                <ItemIcon className="h-3.5 w-3.5 shrink-0" />
                <span className="flex-1">{s.label}</span>
                {active && <Check className="h-3.5 w-3.5 text-[hsl(var(--kf-gold-deep))]" strokeWidth={3} />}
              </button>
            );
          })}
          <div className="border-t hairline-soft" />
          <button
            data-testid="lead-loanstatus-option-clear"
            onClick={() => change(null)}
            className={cn(
              'w-full flex items-center gap-2 px-3 py-2 text-[12.5px] font-semibold transition text-left',
              !current ? 'bg-neutral-50 text-neutral-900' : 'text-neutral-500 hover:bg-neutral-50 hover:text-neutral-900'
            )}
          >
            <X className="h-3.5 w-3.5 shrink-0" />
            <span className="flex-1">No stage</span>
            {!current && <Check className="h-3.5 w-3.5 text-[hsl(var(--kf-gold-deep))]" strokeWidth={3} />}
          </button>
        </div>,
        document.body,
      )}
      {showReadyModal && (
        <ReadyToSubmitModal
          lead={lead}
          onClose={() => setShowReadyModal(false)}
        />
      )}
      {showFundedModal && (
        <FundedConfirmModal
          lead={lead}
          onClose={() => setShowFundedModal(false)}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------
   ReadyToSubmitModal — gate that fires when a broker promotes a lead
   from /leads into /pipeline. Two-step wizard:
     1. Lender info: lender, loan number, close date, channel
     2. Ops setup:   processor, margin (% or $), reimbursement ($)
   All 9 fields PATCH in a single atomic call so the deal never ends up
   half-promoted.
   ------------------------------------------------------------------ */
const PROCESSORS = [
  'Self Processing',
  'Daniel Becke', 'Amber Robinson',
  'Tony Banuelos', 'Myschel Boyd', 'Dalilah Carballo',
  'Alicia Graves', 'Laura Procell', 'Emily Lantz',
  'Katrina Wilson',
];

function ReadyToSubmitModal({ lead, onClose }) {
  // ----- All fields collected on one screen now -----
  const [lender, setLender] = useState(lead.application?.lender || lead.lender || '');
  const [loanNumber, setLoanNumber] = useState(lead.loanNumber || '');
  const [closeDate, setCloseDate] = useState(lead.estimatedClosingDate || '');
  const [channel, setChannel] = useState(lead.loanChannel || '');
  const [processor, setProcessor] = useState(lead.processor || '');
  const [marginType, setMarginType] = useState(lead.marginType || 'dollar');
  const [margin, setMargin] = useState(lead.margin != null ? String(lead.margin) : '');
  const [reimbursement, setReimbursement] = useState(lead.reimbursement != null ? String(lead.reimbursement) : '');
  const [saving, setSaving] = useState(false);

  const isValid = lender.trim() && loanNumber.trim() && closeDate && channel
    && processor && margin !== '' && !isNaN(Number(margin));

  const submit = async () => {
    if (!isValid || saving) return;
    setSaving(true);
    const updated = await LeadsStore.update(lead.id, {
      loanStatus: 'ready_to_submit',
      lender: lender.trim(),
      loanNumber: loanNumber.trim(),
      estimatedClosingDate: closeDate,
      loanChannel: channel,
      application: { ...(lead.application || {}), lender: lender.trim() },
      processor,
      margin: Number(margin),
      marginType,
      reimbursement: reimbursement === '' ? null : Number(reimbursement),
    });
    setSaving(false);
    if (updated) {
      toast.success(`${updated.name} → Pipeline · Ready to Submit`);
      onClose();
    } else {
      toast.error('Could not promote to pipeline');
    }
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div data-testid="ready-to-submit-modal" className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-neutral-900/40 backdrop-blur-[2px]" onClick={onClose} />
      <div className="relative w-[820px] max-w-[96vw] max-h-[94vh] rounded-2xl bg-white border hairline shadow-[0_36px_88px_-32px_rgba(0,0,0,0.45)] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 pt-5 pb-3 border-b hairline-soft shrink-0">
          <div className="text-[10px] uppercase tracking-wider font-bold text-[hsl(var(--kf-gold-deep))]">Promote to Pipeline</div>
          <h2 className="display text-[20px] font-bold tracking-tight text-neutral-900 mt-0.5">Ready to Submit</h2>
          <p className="text-[12.5px] text-neutral-500 mt-1 leading-relaxed">
            Capture the lender + ops details so <span className="font-semibold text-neutral-700">{lead.name}</span> arrives in the pipeline fully populated.
          </p>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-1">
          <div className="grid grid-cols-2 gap-x-6 gap-y-4">
            {/* === LEFT COLUMN === */}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Lender">
                  <input autoFocus data-testid="ready-modal-lender" value={lender} onChange={(e) => setLender(e.target.value)} placeholder="e.g. UWM" className="modal-input" />
                </Field>
                <Field label="Loan #">
                  <input data-testid="ready-modal-loan-number" value={loanNumber} onChange={(e) => setLoanNumber(e.target.value)} placeholder="e.g. 1247853901" className="modal-input tabular-nums" />
                </Field>
              </div>
              <Field label="Estimated Close Date">
                <DatePopover
                  testId="ready-modal-close-date"
                  value={closeDate}
                  onChange={setCloseDate}
                  placeholder="Pick a closing date"
                />
              </Field>
              <Field label="Channel">
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: 'broker',        label: 'Broker',        tone: 'bg-blue-50 text-blue-700 border-blue-200' },
                    { value: 'correspondent', label: 'Correspondent', tone: 'bg-violet-50 text-violet-700 border-violet-200' },
                  ].map((c) => {
                    const active = channel === c.value;
                    return (
                      <button key={c.value} type="button" data-testid={`ready-modal-channel-${c.value}`} onClick={() => setChannel(c.value)}
                        className={cn('h-10 rounded-lg border text-[13px] font-bold transition flex items-center justify-center gap-1.5',
                          active ? cn(c.tone, 'ring-2 ring-offset-1 ring-neutral-900/10') : 'bg-white text-neutral-700 border-neutral-200 hover:border-neutral-400')}>
                        {active && <Check className="h-3.5 w-3.5" strokeWidth={3} />} {c.label}
                      </button>
                    );
                  })}
                </div>
              </Field>
              <Field label="Base Commission">
                <div className="flex items-stretch gap-0">
                  <div className="inline-flex items-stretch rounded-l-lg border border-r-0 hairline overflow-hidden">
                    {[
                      { value: 'percent', label: '%' },
                      { value: 'dollar',  label: '$' },
                    ].map((m) => {
                      const active = marginType === m.value;
                      return (
                        <button
                          key={m.value}
                          type="button"
                          data-testid={`ready-modal-margin-${m.value}`}
                          onClick={() => setMarginType(m.value)}
                          className={cn(
                            'w-9 inline-flex items-center justify-center text-[14px] font-bold transition',
                            active ? 'bg-emerald-500 text-white' : 'bg-white text-neutral-500 hover:text-neutral-900',
                          )}
                        >
                          {m.label}
                        </button>
                      );
                    })}
                  </div>
                  <input
                    type="number"
                    min="0"
                    step={marginType === 'percent' ? '0.001' : '1'}
                    data-testid="ready-modal-margin-value"
                    value={margin}
                    onChange={(e) => setMargin(e.target.value)}
                    placeholder={marginType === 'percent' ? '1.250' : '5019'}
                    className="modal-input flex-1 rounded-l-none tabular-nums"
                  />
                </div>
              </Field>
              <Field label="Reimbursement ($)">
                <input
                  type="number"
                  min="0"
                  step="1"
                  data-testid="ready-modal-reimbursement"
                  value={reimbursement}
                  onChange={(e) => setReimbursement(e.target.value)}
                  placeholder="e.g. 499"
                  className="modal-input tabular-nums"
                />
              </Field>
            </div>

            {/* === RIGHT COLUMN — Processor === */}
            <div>
              <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 mb-2 block">Processor</span>
              <div className="rounded-xl border hairline bg-neutral-50/40 p-3">
                <div className="grid grid-cols-2 gap-2">
                  {PROCESSORS.map((name) => {
                    const active = processor === name;
                    return (
                      <button
                        key={name}
                        type="button"
                        data-testid={`ready-modal-processor-${name.replace(/\s+/g, '-').toLowerCase()}`}
                        onClick={() => setProcessor(name)}
                        className={cn(
                          'h-11 rounded-lg border text-[12.5px] font-bold transition px-2 leading-tight',
                          active
                            ? 'bg-emerald-50 text-emerald-800 border-emerald-400 ring-2 ring-emerald-200'
                            : 'bg-white text-neutral-700 border-neutral-200 hover:border-neutral-400',
                        )}
                      >
                        {name}
                      </button>
                    );
                  })}
                </div>
                {processor && (
                  <div data-testid="ready-modal-processor-selected" className="mt-3 px-3 py-2 rounded-lg bg-emerald-50/70 border border-emerald-200 text-[12px] font-semibold text-emerald-800 flex items-center gap-2">
                    <Check className="h-3.5 w-3.5" strokeWidth={3} />
                    Selected · {processor}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-neutral-50/60 border-t hairline-soft flex items-center justify-end gap-2 shrink-0">
          <button data-testid="ready-modal-cancel" onClick={onClose} className="h-9 px-4 rounded-lg border hairline bg-white text-[12.5px] font-bold text-neutral-700 hover:border-neutral-400 transition">Cancel</button>
          <button
            data-testid="ready-modal-submit"
            onClick={submit}
            disabled={!isValid || saving}
            className={cn('h-9 px-4 rounded-lg text-[12.5px] font-bold transition', isValid && !saving ? 'btn-ink' : 'bg-neutral-200 text-neutral-400 cursor-not-allowed')}
          >{saving ? 'Saving…' : 'Move to Pipeline'}</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 mb-1 block">{label}</span>
      {children}
    </label>
  );
}


/* ------------------------------------------------------------------ */
/* Save status pill — shows whether the latest 1003 edit has actually */
/* persisted to Mongo. Surfaces:                                       */
/*   pending  → user typed within the 800ms debounce window            */
/*   saving   → PATCH inflight                                         */
/*   idle     → nothing pending; latest edit confirmed saved           */
/*   error    → PATCH failed                                           */
/* The point is to give the LO visible confidence that data is safe    */
/* before they hit "Send to ARIVE" / leave the page.                   */
function ResendToAriveModal({ open, leadName, lastPushedAt, pushing, onClose, onConfirm }) {
  // Same modal handles BOTH first-push and resend, with adaptive copy:
  //   - First push: friendly blue confirmation, single-click confirm.
  //   - Resend:    red duplicate-warning, type "RESEND" to enable confirm.
  // Server-side 30s cooldown + 409 confirm-required rules back this up.
  const isResend = !!lastPushedAt;
  const [text, setText] = useState('');
  useEffect(() => { if (open) setText(''); }, [open]);
  if (!open) return null;
  const canConfirm = !pushing && (isResend ? text.trim().toUpperCase() === 'RESEND' : true);
  const lastPushedLabel = (() => {
    if (!lastPushedAt) return null;
    try {
      const d = new Date(lastPushedAt);
      const TZ = 'America/Phoenix';
      return `${d.toLocaleDateString('en-US', { timeZone: TZ })} at ${d.toLocaleTimeString('en-US', { timeZone: TZ, hour: '2-digit', minute: '2-digit' })}`;
    } catch (_) { return null; }
  })();
  return (
    <div
      data-testid={isResend ? 'resend-arive-modal' : 'send-arive-modal'}
      className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg bg-white dark:bg-neutral-950 rounded-2xl border hairline shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-6 pb-2 flex items-start gap-3">
          <span
            className={cn(
              'inline-flex h-10 w-10 items-center justify-center rounded-full border shrink-0',
              isResend
                ? 'bg-rose-50 dark:bg-rose-950/40 border-rose-200 dark:border-rose-500/30'
                : 'bg-sky-50 dark:bg-sky-950/40 border-sky-200 dark:border-sky-500/30'
            )}
          >
            {isResend ? (
              <AlertTriangle className="h-5 w-5 text-rose-600" />
            ) : (
              <Send className="h-5 w-5 text-sky-600" />
            )}
          </span>
          <div className="flex-1">
            <h2 className="text-[17px] font-bold text-neutral-900 dark:text-white">
              {isResend ? 'Resend lead to ARIVE?' : 'Send lead to ARIVE?'}
            </h2>
            <p className="mt-1.5 text-[13px] text-neutral-600 dark:text-neutral-300 leading-snug">
              {isResend ? (
                <>
                  <span className="font-semibold text-neutral-900 dark:text-white">{leadName || 'This lead'}</span>{' '}
                  was already pushed to ARIVE on{' '}
                  <span className="font-semibold">{lastPushedLabel}</span>. ARIVE will create a{' '}
                  <span className="font-bold text-rose-600">brand-new duplicate loan</span> — it does <em>not</em>{' '}
                  recognize that this is the same lead.
                </>
              ) : (
                <>
                  This will create a new loan in ARIVE for{' '}
                  <span className="font-semibold text-neutral-900 dark:text-white">{leadName || 'this lead'}</span>{' '}
                  using the data currently in their profile. Make sure all required fields (loan amount, property,
                  borrower) are accurate before sending.
                </>
              )}
            </p>
          </div>
        </div>
        {isResend && (
          <div className="px-6 pb-3">
            <div className="mt-2 rounded-lg bg-rose-50/70 dark:bg-rose-950/20 border border-rose-100 dark:border-rose-500/20 p-3 text-[12px] text-rose-700 dark:text-rose-300 leading-snug">
              <span className="font-bold">Recommended:</span> only resend if you're sure ARIVE never received the
              first push, or if you need a duplicate intentionally (e.g. testing). Otherwise update the existing
              loan in ARIVE directly.
            </div>
          </div>
        )}
        {isResend && (
          <div className="px-6 pb-2">
            <label className="text-[11px] uppercase tracking-[0.15em] font-bold text-neutral-500 mb-1.5 block">
              Type <span className="text-neutral-900 dark:text-white mono font-bold">RESEND</span> to confirm
            </label>
            <input
              data-testid="resend-arive-confirm-input"
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="RESEND"
              className="w-full h-11 px-3 rounded-md border hairline bg-white dark:bg-neutral-900 text-[14px] font-bold mono tracking-wide focus:outline-none focus:border-rose-400 transition"
              autoFocus
            />
          </div>
        )}
        <div className="px-6 py-4 flex items-center justify-end gap-2 border-t hairline mt-2">
          <button
            data-testid={isResend ? 'resend-arive-cancel' : 'send-arive-cancel'}
            type="button"
            onClick={onClose}
            disabled={pushing}
            className="h-10 px-4 rounded-md border hairline bg-white dark:bg-neutral-900 text-[12.5px] font-bold text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            data-testid={isResend ? 'resend-arive-confirm' : 'send-arive-confirm'}
            type="button"
            onClick={onConfirm}
            disabled={!canConfirm}
            className={cn(
              'h-10 px-4 rounded-md text-white text-[12.5px] font-bold flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed',
              isResend ? 'bg-rose-600 hover:bg-rose-700' : 'btn-ink'
            )}
          >
            <Send className="h-4 w-4" />
            {pushing
              ? (isResend ? 'Resending…' : 'Sending…')
              : (isResend ? 'Create duplicate in ARIVE' : 'Send to ARIVE')}
          </button>
        </div>
      </div>
    </div>
  );
}



function SaveStatusPill({ status, lastSavedAt }) {
  // Tick once a minute so "Saved 1m ago" → "Saved 2m ago" updates without
  // requiring an unrelated re-render.
  const [, force] = useState(0);
  useEffect(() => {
    if (status !== 'idle' || !lastSavedAt) return;
    const t = setInterval(() => force((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, [status, lastSavedAt]);

  if (status === 'pending') {
    return (
      <span
        data-testid="save-status-pill"
        data-status="pending"
        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border bg-amber-50 text-amber-700 border-amber-200 text-[11.5px] font-bold"
        title="Your latest edit is queued to save in under a second."
      >
        <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
        Unsaved…
      </span>
    );
  }
  if (status === 'saving') {
    return (
      <span
        data-testid="save-status-pill"
        data-status="saving"
        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border bg-sky-50 text-sky-700 border-sky-200 text-[11.5px] font-bold"
        title="Saving your edit to the server…"
      >
        <span className="h-1.5 w-1.5 rounded-full bg-sky-500 animate-pulse" />
        Saving…
      </span>
    );
  }
  if (status === 'error') {
    return (
      <span
        data-testid="save-status-pill"
        data-status="error"
        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border bg-rose-50 text-rose-700 border-rose-300 text-[11.5px] font-bold"
        title="Last save failed. Edit any field to retry."
      >
        <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />
        Save failed
      </span>
    );
  }
  if (!lastSavedAt) return null;
  const ago = Math.max(0, Math.round((Date.now() - lastSavedAt) / 1000));
  const label =
    ago < 5     ? 'Saved · just now' :
    ago < 60    ? `Saved · ${ago}s ago` :
    ago < 3600  ? `Saved · ${Math.round(ago / 60)}m ago` :
                  `Saved · ${Math.round(ago / 3600)}h ago`;
  return (
    <span
      data-testid="save-status-pill"
      data-status="idle"
      className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border bg-emerald-50 text-emerald-700 border-emerald-200 text-[11.5px] font-bold"
      title="All your edits have been written to the server."
    >
      <CheckCircle2 className="h-3.5 w-3.5" />
      {label}
    </span>
  );
}


/* ------------------------------------------------------------------ */
/* Lead actions menu — Withdraw / Reinstate / Deny / DNC              */
/* ------------------------------------------------------------------ */
const DENIAL_REASONS = [
  { key: 'income',   label: 'Income',   blurb: 'DTI too high, insufficient docs, qualifying income too low.' },
  { key: 'credit',   label: 'Credit',   blurb: 'Score below program min, recent derogatory, BK/foreclosure.' },
  { key: 'property', label: 'Property', blurb: 'Appraisal short, ineligible property type, condition issues.' },
  { key: 'assets',   label: 'Assets',   blurb: 'Reserves too thin, large unsourced deposits, gift docs missing.' },
];

function LeadActionsMenu({ lead, navigate, pushToArive, pushingArive, isAdmin }) {
  const [open, setOpen] = useState(false);
  // `view` controls which sub-panel is visible inside the menu.
  // 'root' = main option list; 'deny' = pick a denial reason.
  const [view, setView] = useState('root');
  // Reassign opens a centered dialog (rendered separately so it survives
  // the menu close).
  const [reassignOpen, setReassignOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setView('root');
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const closeAll = () => { setOpen(false); setView('root'); };

  const withdraw = async () => {
    closeAll();
    const ok = window.confirm(`Withdraw ${lead.name}? They'll be hidden from your active leads pipeline. You can reinstate them later.`);
    if (!ok) return;
    // Also yank them out of the outbound dialer — a withdrawn lead
    // should never re-surface on someone's call queue. The outbound
    // history / attempts stay intact for reference; only the flag flips.
    const updated = await LeadsStore.update(lead.id, {
      withdrawn: true,
      withdrawnAt: new Date().toISOString(),
      outbound: false,
    });
    if (updated) { toast.success(`${lead.name} withdrawn from pipeline`); navigate('/leads'); }
    else toast.error('Could not withdraw lead');
  };

  const reinstate = async () => {
    closeAll();
    const updated = await LeadsStore.update(lead.id, {
      withdrawn: false, withdrawnAt: null,
      denied: false, deniedReason: null, deniedAt: null,
    });
    if (updated) toast.success(`${lead.name} is back in your active pipeline`);
    else toast.error('Could not reinstate lead');
  };

  const deny = async (reason) => {
    closeAll();
    // Denied leads also leave the dialer — no more cold-calling once
    // underwriting killed the file.
    const updated = await LeadsStore.update(lead.id, {
      denied: true,
      deniedReason: reason.key,
      deniedAt: new Date().toISOString(),
      outbound: false,
    });
    if (updated) { toast.success(`${lead.name} marked denied — ${reason.label}`); navigate('/leads'); }
    else toast.error('Could not mark denied');
  };

  const addToDnc = async () => {
    closeAll();
    const ok = window.confirm(`Opt out ${lead.name} from all outreach? They'll be hidden from every list and logged in the Opt Out reporting.`);
    if (!ok) return;
    // Opt Out is the strongest suppression: hide from all lists AND the
    // dialer. Mirrors the Focus Mode "Opt Out" button behavior.
    const updated = await LeadsStore.update(lead.id, { temp: 'dnc', outbound: false });
    if (updated) toast.success(`${lead.name} opted out`);
    else toast.error('Could not update lead');
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid="lead-actions-trigger"
        onClick={() => { setOpen((v) => !v); setView('root'); }}
        className="h-9 w-9 rounded-md border hairline bg-white hover:border-neutral-400 flex items-center justify-center text-neutral-700"
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>

      {open && view === 'root' && (
        <div
          data-testid="lead-actions-menu"
          className="absolute z-30 top-full mt-1.5 right-0 w-[280px] rounded-xl border hairline bg-white shadow-[0_18px_48px_-18px_rgba(0,0,0,0.25)] overflow-hidden"
        >
          {lead.withdrawn || lead.denied ? (
            <ActionRow
              testId="lead-action-reinstate"
              onClick={reinstate}
              icon={RotateCcw}
              iconTone="text-[hsl(var(--kf-gold-deep))]"
              title="Reinstate lead"
              blurb={lead.denied ? 'Clear the denial and move back into the active pipeline.' : 'Move back into the active pipeline.'}
            />
          ) : (
            <>
              <ActionRow
                testId="lead-action-arive"
                onClick={() => { closeAll(); pushToArive && pushToArive(); }}
                icon={Cloud}
                iconTone={lead?.arivePushedAt ? 'text-emerald-600' : 'text-neutral-700'}
                title={lead?.arivePushedAt ? 'Resend to ARIVE' : 'Send to ARIVE'}
                blurb={
                  lead?.arivePushedAt
                    ? `Last pushed ${new Date(lead.arivePushedAt).toLocaleString()}. Click to resend.`
                    : 'Push this lead to ARIVE via the configured Zapier webhook.'
                }
                disabled={pushingArive}
              />
              <div className="h-px bg-neutral-100" />
              <ActionRow
                testId="lead-action-reassign"
                onClick={() => { closeAll(); setReassignOpen(true); }}
                icon={Shuffle}
                iconTone="text-neutral-700"
                title="Reassign loan officer"
                blurb="Hand this lead off to another LO on the team."
              />
              <div className="h-px bg-neutral-100" />
              <ActionRow
                testId="lead-action-withdraw"
                onClick={withdraw}
                icon={Archive}
                iconTone="text-neutral-700"
                title="Withdraw lead"
                blurb="Hide from pipeline — preserve all data, reinstate anytime."
              />
              <div className="h-px bg-neutral-100" />
              <ActionRow
                testId="lead-action-deny"
                onClick={() => setView('deny')}
                icon={ShieldX}
                iconTone="text-red-600"
                title="Deny lead"
                blurb="Mark as denied by underwriting — pick a reason next."
                chevron
              />
              <div className="h-px bg-neutral-100" />
              <ActionRow
                testId="lead-action-dnc"
                onClick={addToDnc}
                icon={Ban}
                iconTone="text-neutral-900"
                title="Opt out"
                blurb="Remove from every list — logged in the Opt Out reporting."
              />
            </>
          )}
        </div>
      )}

      {open && view === 'deny' && (
        <div
          data-testid="lead-deny-menu"
          className="absolute z-30 top-full mt-1.5 right-0 w-[320px] rounded-xl border hairline bg-white shadow-[0_18px_48px_-18px_rgba(0,0,0,0.25)] overflow-hidden"
        >
          <div className="px-3 py-2.5 flex items-center gap-2 border-b hairline-soft">
            <button
              onClick={() => setView('root')}
              className="h-6 w-6 rounded hover:bg-neutral-100 flex items-center justify-center text-neutral-500"
              title="Back"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
            </button>
            <div className="text-[12px] font-bold text-neutral-900">Reason for denial</div>
          </div>
          {DENIAL_REASONS.map((r, i) => {
            const Icon = { income: DollarSign, credit: CreditCard, property: Building2, assets: Wallet }[r.key];
            return (
              <button
                key={r.key}
                data-testid={`lead-deny-reason-${r.key}`}
                onClick={() => deny(r)}
                className={cn(
                  'w-full flex items-start gap-2.5 px-3 py-2.5 text-left transition hover:bg-red-50',
                  i > 0 && 'border-t hairline-soft'
                )}
              >
                <span className="h-7 w-7 shrink-0 rounded-md bg-red-50 text-red-600 flex items-center justify-center mt-0.5">
                  <Icon className="h-3.5 w-3.5" strokeWidth={2.5} />
                </span>
                <span className="flex-1">
                  <span className="block text-[12.5px] font-bold text-neutral-900">{r.label}</span>
                  <span className="block text-[10.5px] font-normal text-neutral-500 mt-0.5">{r.blurb}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}

      {reassignOpen && <ReassignDialog lead={lead} onClose={() => setReassignOpen(false)} />}
    </div>
  );
}

function ActionRow({ testId, onClick, icon: Icon, iconTone, title, blurb, chevron, disabled }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'w-full flex items-start gap-2.5 px-3 py-2.5 text-[12.5px] font-semibold text-left transition',
        disabled
          ? 'text-neutral-400 cursor-not-allowed hover:bg-transparent'
          : 'text-neutral-800 hover:bg-neutral-100',
      )}
    >
      <Icon className={cn('h-4 w-4 mt-0.5 shrink-0', iconTone)} />
      <span className="flex-1">
        <span className="block">{title}</span>
        <span className="block text-[10.5px] font-normal text-neutral-500 mt-0.5">{blurb}</span>
      </span>
      {chevron && <ChevronDown className="h-3.5 w-3.5 -rotate-90 text-neutral-400 mt-1" />}
    </button>
  );
}


function LeadCreatedChip({ createdAt }) {
  if (!createdAt) return null;
  const d = new Date(createdAt);
  if (Number.isNaN(d.getTime())) return null;
  const TZ = 'America/Phoenix';
  const fullDate = d.toLocaleDateString('en-US', { timeZone: TZ, month: 'short', day: 'numeric', year: 'numeric' });
  const fullTime = d.toLocaleTimeString('en-US', { timeZone: TZ, hour: 'numeric', minute: '2-digit' });
  return (
    <div
      data-testid="lead-created-chip"
      title={`Created ${fullDate} at ${fullTime} — this value is immutable`}
      className="inline-flex items-center gap-2 rounded-full border hairline bg-neutral-50 px-3 py-1.5 select-none"
    >
      <CalendarPlus className="h-3.5 w-3.5 text-neutral-500" strokeWidth={2} />
      <div className="leading-tight">
        <div className="text-[9.5px] uppercase tracking-[0.18em] font-bold text-neutral-400">Lead Created</div>
        <div className="text-[12px] font-semibold text-neutral-800 display tracking-tight">
          {fullDate}
        </div>
      </div>
    </div>
  );
}


function LoanSummaryView({ lead, data }) {
  const borrowerName = [data.firstName, data.middleName, data.lastName]
    .filter(Boolean).join(' ').trim() || lead?.name || '—';
  const propertyAddress = (() => {
    const street = data.propertyAddress || '';
    const cityStateZip = [
      data.propertyCity,
      data.propertyState,
      data.propertyZip,
    ].filter(Boolean).join(', ').replace(', ' + (data.propertyZip || ''), ' ' + (data.propertyZip || '')).trim();
    const out = [street, cityStateZip].filter(Boolean).join(', ');
    return out || '—';
  })();
  const rate = Number(data.rate) ? `${Number(data.rate).toFixed(3)}%` : '—';
  const rows = [
    { label: 'Borrower', value: borrowerName, mono: false },
    { label: 'Property Address', value: propertyAddress, mono: false },
    { label: 'Property Type', value: data.propertyType || '—' },
    { label: 'Property Use', value: data.occupancy || '—' },
    { label: 'Loan Date', value: data.loanDate || '—', mono: true },
    { label: 'Lender', value: data.lender || '—' },
    { label: 'Loan Type', value: data.loanType || '—' },
    { label: 'Loan Purpose', value: data.loanPurpose || '—' },
    { label: 'Rate', value: rate, mono: true },
  ];
  return (
    <div className="space-y-4" data-testid="loan-summary-view">
      <div>
        <h3 className="display text-[22px] font-bold text-neutral-900 tracking-tight leading-tight">Loan Summary</h3>
        <p className="text-[12.5px] text-neutral-500 mt-0.5">Snapshot of the borrower, property, and loan terms.</p>
      </div>

      <div className="rounded-2xl border hairline bg-white overflow-hidden">
        <div className="px-6 py-4 border-b hairline">
          <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-400 font-bold">Loan File</div>
          <div className="display text-[16px] font-bold text-neutral-900 tracking-tight mt-1">Terms &amp; Party Information</div>
        </div>
        <div className="divide-y hairline">
          {rows.map((r) => (
            <div key={r.label} className="grid grid-cols-12 items-center px-6 py-3.5">
              <div className="col-span-4 text-[10.5px] uppercase tracking-[0.18em] font-bold text-neutral-500">
                {r.label}
              </div>
              <div className={cn(
                'col-span-8 text-[14px] font-semibold text-neutral-900 break-words',
                r.mono && 'display tracking-tight'
              )} data-testid={`loan-summary-${r.label.toLowerCase().replace(/\s+/g, '-')}`}>
                {r.value}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}



